// @flow
import { isMemoryOnlyToken, withoutTokens } from 'src/latest/src/auth/authStore';
import { StorageKey } from 'src/latest/src/constants/StorageKey';
import { onGetStorage } from '../../legacy/utils/helpers';
import Storage from '../../latest/src/utils/Storage';

const AUTHENTICATE_USER = 'AUTHENTICATE_USER';
const SET_PROFILE_DATA = 'SET_PROFILE_DATA';
const SET_LOGGING_OUT = 'SET_LOGGING_OUT';
const SET_USER_ROLE = 'SET_USER_ROLE';
const SET_USER_FULLNAME = 'SET_USER_FULLNAME';

export function authUser(data: Object): {data: Object} {
    return {
        type: AUTHENTICATE_USER,
        data
    };
}

export function updateLoggedInUser(data: Object): {data: Object} {
    return {
        type: SET_PROFILE_DATA,
        data
    };
}

export function setUserFullName(fullName:string,nameType:string){
    return {
        type: SET_USER_FULLNAME,
        fullName,
        nameType
    };
}

export function setLoggingOut(): any {
    return {
        type: SET_LOGGING_OUT
    };
}

export function setUserRole(userRole: string): {userRole: string} {
    return {
        type: SET_USER_ROLE,
        userRole
    };
}

// This slice owns session identity: who the user is and which role they are
// acting as. The credential is owned by authStore and never enters redux.
// Login flows still hand whole /token responses to authUser(), so everything
// written here goes through withoutTokens() first.
const persistLoggedInUser = (loggedInUser: Object) => {
    // Phase 4: identity is rebuilt from the profile after every silent refresh,
    // so none of it — name, avatar, user_type — is written to disk any more.
    if (isMemoryOnlyToken) {
        return;
    }

    Storage.setItem('loggedInUser', JSON.stringify(loggedInUser));
};

// The one piece of identity that still persists: which role the user switched
// to. It is a UI choice rather than session data, and without it a practice
// admin lands back in their default role on every reload.
const persistActiveRole = (userRole: string) => {
    Storage.setItem(StorageKey.activeRole, userRole?.toLowerCase());
};

const initialState = {
    // With a memory-only session, being logged in is a consequence of a
    // successful refresh at startup. Seeding it from storage would render the
    // app as authenticated with no token behind it, and every call would 401.
    isAuthed: isMemoryOnlyToken ? false : onGetStorage('isAuthed', false),
    isLoggingOut: onGetStorage('isLoggingOut', false),
    // sessions from before Phase 2 persisted tokens under this key too
    loggedInUser: isMemoryOnlyToken
        ? {}
        : withoutTokens(onGetStorage('loggedInUser', {}))
};

export default function authentication(state: Object = initialState, action: Object):|any| {
    isAuthed: boolean,
    isLoggingOut: boolean,
    loggedInUser: {}
  } {
    switch (action.type) {
        case AUTHENTICATE_USER: {
            const loggedInUser = withoutTokens(action.data);

            if (!isMemoryOnlyToken) {
                Storage.setItem('isAuthed', true);
            }

            persistLoggedInUser(loggedInUser);

            return {
                ...state,
                loggedInUser,
                isAuthed: true,
                isLoggingOut: false
            };
        }

        case SET_PROFILE_DATA: {
            const loggedInUser = withoutTokens({
                ...state.loggedInUser,
                ...action.data
            });

            persistLoggedInUser(loggedInUser);

            return {
                ...state,
                loggedInUser
            };
        }

        case SET_USER_FULLNAME:
            return {
                ...state,
                loggedInUser: {
                    ...state.loggedInUser,
                    ...(action.nameType === 'firstname' &&{firstName: action.fullName}),
                    ...(action.nameType === 'lastname' &&{lastName: action.fullName})
                }
            };
        case SET_LOGGING_OUT:
            return {
                isAuthed: false,
                loggedInUser: {},
                isLoggingOut: true
            };

        case SET_USER_ROLE: {
            const loggedInUser = {
                ...state.loggedInUser,
                userRole: action.userRole
            };

            // Persisted so a role switch survives a reload, which it used to do
            // by being written into the token blob. Lowercase is the form that
            // blob held and the startup redirect matches on; the in-session
            // value keeps the caller's casing, as it always has.
            persistActiveRole(action.userRole);
            persistLoggedInUser({
                ...loggedInUser,
                userRole: action.userRole?.toLowerCase()
            });

            return {
                ...state,
                loggedInUser
            };
        }

        default:
            return state;
    }
}
